import {
  DataDrivenPropertyValueSpecification,
  FilterSpecification,
  LayerProps,
} from 'shared/map-exports';
import { FONT_NAMES } from 'features/mapping/styles';
import { ActiveFloodIcon } from 'features/mapping/icons';

/**
 * Get the opacity for a geo event icon. Hides the icon when in edit mode and
 * shows it when selected. Otherwise, falls back to 'opacity' property value.
 */
export const getIconOpacityExpression = (
  selectedGeoEventId: number,
  isEdit: boolean,
): DataDrivenPropertyValueSpecification<number> => [
  'case',
  [
    'all',
    ['==', ['get', 'id'], selectedGeoEventId],
    ['==', ['get', 'opacity'], 0],
  ],
  1,
  // hide this icon on the edit page since <FloodingMarker> appears instead
  [
    'case',
    ['all', ['==', isEdit, true], ['==', ['get', 'id'], selectedGeoEventId]],
    0,
    ['get', 'opacity'],
  ],
];

export const getIconStyle = (
  selectedGeoEventId: number,
  isEdit: boolean,
): LayerProps => ({
  type: 'symbol',
  layout: {
    'icon-image': [
      'case',
      ['==', ['get', 'id'], selectedGeoEventId],
      ['get', 'selectedIcon'],
      ['get', 'icon'],
    ],
    'icon-allow-overlap': true,
    'icon-anchor': 'bottom',
    'icon-ignore-placement': false,
    'icon-size': [
      'interpolate',
      ['linear'],
      ['zoom'],
      4,
      [
        'match',
        ['get', 'icon'],
        ActiveFloodIcon.name,
        ['*', ['get', 'scale'], 0.9],
        ['*', ['get', 'scale'], 0.6],
      ],
      8,
      ['get', 'scale'],
    ],
    'icon-offset': [
      ['get', 'offsetX'],
      ['get', 'offsetY'],
    ],
    // For some reason, the icons need to be sorted by 'index',
    // but the labels by 'indexInv', in order to be sorted correctly
    // in both cases.
    'symbol-sort-key': ['get', 'index'],
    'symbol-z-order': 'source',
  },
  paint: {
    'icon-opacity': getIconOpacityExpression(selectedGeoEventId, isEdit),
  },
});

const labelStyleCommon: LayerProps = {
  type: 'symbol',
  layout: {
    'text-font': FONT_NAMES.regular,
    'text-field': [
      'format',
      ['get', 'name'],
      { 'text-font': ['literal', FONT_NAMES.bold] },
    ],
    'text-anchor': 'top',
    'text-justify': 'center',
    'symbol-sort-key': ['get', 'indexInv'],
    'symbol-z-order': 'source',
    'icon-image': '',
    'icon-text-fit': 'both',
    'text-size': 16,
    'text-offset': [0, 0],
  },
  paint: {
    'text-halo-width': 1.8,
  },
};

const labelStyleLight: LayerProps = {
  ...labelStyleCommon,
  paint: {
    ...labelStyleCommon.paint,
    'text-color': '#333333',
    'text-halo-color': 'rgba(255,255,255,0.8)',
  },
};

const labelStyleDark: LayerProps = {
  ...labelStyleCommon,
  paint: {
    ...labelStyleCommon.paint,
    'text-color': '#ffffff',
    'text-halo-color': 'rgba(0,0,0,0.8)',
  },
};

/**
 * Ensure selected label is always on top if selected.
 */
const getLabelSortExpression = (
  selectedGeoEventId: number,
): DataDrivenPropertyValueSpecification<number> => [
  'case',
  ['==', ['get', 'id'], selectedGeoEventId],
  -99999,
  ['get', 'indexInv'],
];

/**
 * Control label visibility via a filter.
 *
 * @returns a boolean expression that renders the element when zoomed in beyond
 * labelMinZoom and filters it out if not. Selected geo event elements are
 * always visible. Active and inactive layers pass different labelMinZoom values
 * so inactive labels only appear at a higher zoom.
 */
export const getLabelFilterExpression = (
  selectedGeoEventId: number,
  labelMinZoom: number,
): FilterSpecification => [
  'step',
  ['zoom'],
  // below labelMinZoom: only show if selected
  ['==', ['get', 'id'], selectedGeoEventId],
  labelMinZoom,
  // at labelMinZoom and above: show if selected OR if associated icon has any opacity
  [
    'any',
    ['==', ['get', 'id'], selectedGeoEventId],
    ['!=', ['get', 'opacity'], 0],
  ],
];

export const getLabelStyle = (
  lightBaseLayer: boolean,
  selectedGeoEventId: number,
  labelMinZoom: number,
): LayerProps => {
  const { layout, ...style } = lightBaseLayer
    ? labelStyleLight
    : labelStyleDark;

  return {
    ...style,
    layout: {
      ...layout,
      'symbol-sort-key': getLabelSortExpression(selectedGeoEventId),
    },
    paint: {
      ...style.paint,
    },
    filter: getLabelFilterExpression(selectedGeoEventId, labelMinZoom),
  };
};
